import { channelPath, isChannelSlug, type ChannelSummaryDto } from '@dijitalapp/shared';

/**
 * A group's or channel's photo address from its id and its photo's change time (null when it has none: initials
 * only, nothing asked). The change time is part of the address, so a new photo shows everywhere at once. The
 * server shows a public channel's photo (or one with a public @name) to anyone, others only to members.
 */
export function conversationPhoto(id: string, avatarUpdatedAt: string | null | undefined, large = false): string | null {
  return avatarUpdatedAt ? `/api/v1/groups/${id}/avatar?size=${large ? 'lg' : 'sm'}&v=${Date.parse(avatarUpdatedAt)}` : null;
}

/** Channels to find: the public directory (most followed first), verified ones leading, without those I follow. */
export function pickSuggested(directory: ChannelSummaryDto[], following: Set<string>, max: number): ChannelSummaryDto[] {
  return directory
    .filter((c) => !c.subscribed && !following.has(c.id))
    .sort((a, b) => Number(Boolean(b.verified)) - Number(Boolean(a.verified)) || b.subscriberCount - a.subscriberCount)
    .slice(0, max);
}

/** The name in one of our own /@name links (a person, a group or a channel), or null. host: our own. */
export function handleOfLink(url: string, host: string): string | null {
  if (!URL.canParse(url)) return null;
  const u = new URL(url);
  if (u.host !== host) return null;
  const m = /^\/@([A-Za-z0-9_.]{3,32})\/?$/.exec(u.pathname);
  return m ? m[1]!.toLowerCase() : null;
}

/** The slug in one of our own channel links (/kanal/<slug>, or the /channel/<slug> alias), or null. host: our own. */
export function channelSlugOfLink(url: string, host: string): string | null {
  if (!URL.canParse(url)) return null;
  const u = new URL(url);
  if (u.host !== host) return null;
  const m = /^\/(?:kanal|channel)\/([A-Za-z0-9-]{3,32})\/?$/.exec(u.pathname);
  const slug = m ? m[1]!.toLowerCase() : null;
  return slug && isChannelSlug(slug) ? slug : null;
}

/** A channel's link to copy or share (origin: the web app's, e.g. window.location.origin). */
export const channelLink = (origin: string, slug: string) => `${origin}${channelPath(slug)}`;

/** The link a group or channel is shared by: a channel's /kanal/ link, else an @name (groups, and channels that
 * only have an old one), else null. */
export function shareLinkOf(origin: string, group: { slug?: string | null; handle?: string | null } | null | undefined): string | null {
  if (group?.slug) return channelLink(origin, group.slug);
  if (group?.handle) return `${origin}/@${group.handle}`;
  return null;
}
